import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LucideArrowLeft } from '@lucide/angular';
import { Subscription } from 'rxjs';
import { describeGenericApiError } from '../../../core/http/api-error.util';
import { Task } from '../../tasks/task.model';
import { TaskService } from '../../tasks/task.service';
import { TaskItemsComponent } from '../../tasks/task-items.component';
import { DayStore } from '../day.store';

@Component({
  selector: 'app-task-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, TaskItemsComponent, LucideArrowLeft],
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss']
})
export class TaskDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly taskService = inject(TaskService);
  private readonly dayStore = inject(DayStore);

  private routeParamSubscription: Subscription | null = null;
  private routeQueryParamSubscription: Subscription | null = null;

  readonly activityTypeId = signal<string>(this.route.snapshot.paramMap.get('activityTypeId') ?? '');
  readonly taskId = signal<string>(this.route.snapshot.paramMap.get('taskId') ?? '');
  readonly fromQuery = signal<string | null>(this.route.snapshot.queryParamMap.get('from'));
  readonly fromHabits = signal(this.resolveHabitContext(this.route.snapshot.queryParamMap.get('from')));
  readonly task = signal<Task | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** The activity or habit this task belongs to, by name, once the shared store knows it. */
  readonly parentName = computed(() => {
    const id = this.activityTypeId();
    const name = id ? this.dayStore.getActivityName(id) : '';
    return name && name !== id ? name : null;
  });
  readonly backLabel = computed(() => {
    const name = this.parentName();
    if (name) {
      return `Back to ${name}`;
    }
    return this.fromHabits() ? 'Back to habit' : 'Back to activity';
  });
  readonly backCommands = computed(() => (
    this.fromHabits()
      ? ['/today/habit', this.activityTypeId()]
      : ['/today/allocation', this.activityTypeId()]
  ));
  readonly backQueryParams = computed(() => (
    this.fromQuery() ? { from: this.fromQuery() } : {}
  ));

  ngOnInit(): void {
    this.loadTask();
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      const nextActivityTypeId = params.get('activityTypeId') ?? '';
      const nextTaskId = params.get('taskId') ?? '';
      if (nextActivityTypeId === this.activityTypeId() && nextTaskId === this.taskId()) {
        return;
      }
      this.activityTypeId.set(nextActivityTypeId);
      this.taskId.set(nextTaskId);
      this.loadTask();
    });
    this.routeQueryParamSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      const from = queryParams.get('from');
      this.fromQuery.set(from);
      this.fromHabits.set(this.resolveHabitContext(from));
    });
  }

  ngOnDestroy(): void {
    this.routeParamSubscription?.unsubscribe();
    this.routeQueryParamSubscription?.unsubscribe();
  }

  goBack(): void {
    this.router.navigate(this.backCommands(), {
      queryParams: this.backQueryParams()
    });
  }

  retry(): void {
    this.loadTask();
  }

  private loadTask(): void {
    if (!this.taskId()) {
      this.task.set(null);
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.taskService.get(this.taskId()).subscribe({
      next: (task) => {
        this.task.set(task);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load task detail'));
        this.loading.set(false);
      }
    });
  }

  private resolveHabitContext(fromQuery: string | null): boolean {
    const routePath = this.route.snapshot.routeConfig?.path ?? '';
    if (routePath.startsWith('today/habit/')) {
      return true;
    }
    return (fromQuery ?? '').trim().toLowerCase() === 'habits';
  }
}
